import React from "react";
import {
  Page,
  Text,
  Image,
  Document,
  StyleSheet,
  View,
} from "@react-pdf/renderer";
import Logo from "../../assets/main.png";
import { PrimaryColor, White } from "../../shared/styles/colors";
import EstimateModel from "../../model/estimate";
import DateConverter from "../../utils/DateConverter";
import BundleItem from "../../model/bundleItem";
import Misc from "../../utils/Misc";

const styles = StyleSheet.create({
  header: {
    fontSize: 14,
    fontWeight: "bold",
  },
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
    fontSize: 12,
  },
  infoView: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  infoText: {
    color: "rgba(0, 0, 0, 0.7)",
  },
  text: {
    textAlign: "justify",
  },
  image: {
    position: "absolute",
    width: "140px",
    height: "50px",
    right: 30,
    top: 45,
  },
  moreInfoView: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    marginTop: "2.5%",
  },
  estText: {
    fontSize: 16,
    fontWeight: "bold",
    color: PrimaryColor,
    marginBottom: 10,
  },
  colView: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "30%",
  },
  rowView: {
    display: "flex",
    flexDirection: "row",
    marginLeft: "20%",
  },
  greyText: {
    color: "grey",
  },
  darkBlueTex: {
    color: PrimaryColor,
    fontWeight: "bold",
  },
  boldText: {
    fontWeight: "bold",
  },
  lightBlueView: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    marginTop: "5%",
    color: White,
    backgroundColor: PrimaryColor,
    justifyContent: "space-between",
    padding: 5,
  },
  tableBody: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    padding: 10,
  },
  materialNoteView: {
    width: "75%",
    marginTop: "5%",
    fontSize: 10,
  },
  tableEnd: {
    width: "100%",
    marginTop: "5%",
    justifyContent: "space-evenly",
    color: "grey",
  },
  totalAmountView: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
  },
  totalAmountText: {
    fontSize: 14,
    fontWeight: 900,
  },
  notesView: {
    width: "65%",
    marginTop: "5%",
    color: "grey",
    fontSize: 10,
  },
  additionalNotesView: {
    width: "65%",
    marginTop: "5%",
    marginBottom: "5%",
    color: "grey",
    fontSize: 10,
  },
  noteText: {
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.7)",
  },
  pageNumber: {
    position: "absolute",
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
  },
  acceptView: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  acceptSpacedView: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    width: "50%",
  },
  discountView: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    color: "lightgrey",
    fontSize: 12,
    marginTop: 10,
  },
});

interface Props {
  estimate: EstimateModel;
}

const EstimatePreview = ({ estimate }: Props) => {
  const HR = () => {
    return (
      <View
        style={{
          borderBottomWidth: 1,
          borderBottomColor: "#ccc",
          width: "100%",
          marginVertical: 10,
        }}
      />
    );
  };

  return (
    <Document>
      {estimate && (
        <Page size="A4" style={styles.body}>
          <Text style={styles.estText}>ESTIMATE</Text>
          <View style={styles.infoView}>
            <Text style={styles.header}>Setup Remodeling Inc.</Text>
            <Text style={styles.infoText}>72 Cash St</Text>
            <Text style={styles.infoText}>Fall River, MA 02723</Text>
            <Text style={styles.infoText}>sales@setupremodeling.com</Text>
            <Text style={styles.infoText}>+1 (800)-530-4099</Text>
            <Text style={styles.infoText}>www.setupremodeling.com</Text>
          </View>
          <View style={styles.moreInfoView}>
            <View style={styles.lightBlueView}>
              <Text style={styles.boldText}>Bill to:</Text>
              <Text style={styles.infoText}>
                {`${estimate?.Client?.firstName} ${estimate?.Client?.lastName}`}
              </Text>
              <Text style={styles.infoText}>{estimate?.Client?.street}</Text>
              <Text style={styles.infoText}>
                {estimate.Client?.street && (
                  <span>{estimate.Client.street}</span>
                )}
                {estimate.Client?.city && <span>, {estimate.Client.city}</span>}
                {estimate.Client?.state && (
                  <span>, {estimate.Client.state}</span>
                )}
              </Text>
            </View>
            <HR />
            <View style={styles.lightBlueView}>
              <Text style={styles.boldText}>Estimate details</Text>
              <Text style={styles.infoText}>Estimate no.: {estimate.code}</Text>
              <Text style={styles.infoText}>
                Estimate date: {DateConverter.toFullDate(estimate.createdAt)}
              </Text>
            </View>
          </View>
          <Image style={styles.image} src={Logo} />
          <View>
            {/** Table Header */}
            <View style={styles.tableHeader}>
              <Text>#</Text>
              <Text>Date</Text>
              <Text>Product or Service</Text>
              <Text>Description</Text>
              <Text>Qty</Text>
              <Text>Rate</Text>
              <Text>Amount</Text>
            </View>
            {/** Table Body */}
            {estimate?.items?.map((item: any, index: number) => {
              return (
                <>
                  {/** Parent Rows */}
                  <View style={styles.tableBody} key={item.id}>
                    <View style={{ flex: 1, flexWrap: "wrap" }}>
                      <Text>{index + 1}</Text>
                    </View>
                    <View style={{ flex: 1, flexWrap: "wrap" }}>
                      <Text>{item.task}</Text>
                    </View>
                    <View style={{ flex: 2, flexWrap: "wrap" }}>
                      <Text>{item.description}</Text>
                    </View>
                    <View style={{ flex: 1, textAlign: "right" }}>
                      <Text>{item.quantity}</Text>
                    </View>
                    <View style={{ flex: 1, textAlign: "right" }}>
                      <Text>{item.rate}</Text>
                    </View>
                    <View style={{ flex: 1, textAlign: "right" }}>
                      <Text>{Misc.toUSD(item.amount)}</Text>
                    </View>
                  </View>
                  {/** Child Rows */}
                  <View style={{ marginLeft: 10, color: "grey" }}>
                    {item.BundleItems.map((child: BundleItem) => (
                      <View style={styles.tableBody} key={child.id}>
                        <View style={{ flex: 1, flexWrap: "wrap" }}>
                          <Text></Text>
                        </View>
                        <View style={{ flex: 1, flexWrap: "wrap" }}>
                          <Text>{child.Product?.name}</Text>
                        </View>
                        <View style={{ flex: 2, flexWrap: "wrap" }}>
                          <Text>{child.Product?.description}</Text>
                        </View>
                        <View style={{ flex: 1, textAlign: "right" }}>
                          <Text>{child.quantity}</Text>
                        </View>
                        <View style={{ flex: 1, textAlign: "right" }}>
                          <Text>{child.Product?.price}</Text>
                        </View>
                        <View style={{ flex: 1, textAlign: "right" }}>
                          <Text>
                            {Misc.toUSD(child.Product.price * child.quantity)}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                  {/** Adding a horizontal line after each item */}
                  {index < (estimate?.items?.length ?? 0) - 1 && <HR />}
                </>
              );
            })}
            {/** Note */}
            <View style={styles.materialNoteView}>
              <Text style={styles.infoText}>
                Note: All the works described above are included in this budget,
                those not mentioned will be extra work to be billed separately.
              </Text>
            </View>
            <HR />
          </View>
          <View style={styles.totalAmountView}>
            <Text style={styles.boldText}>TOTAL</Text>
            <Text style={styles.darkBlueTex}>{Misc.toUSD(estimate.total)}</Text>
          </View>
          {estimate.discount > 0 && (
            <View style={styles.discountView}>
              <Text>DISCOUNT</Text>
              <Text>{estimate.discount}%</Text>
            </View>
          )}
          {/** Additional notes */}
          <View style={styles.additionalNotesView}>
            <Text style={styles.infoText}>
              {`Note: 
              Payments to Setup Remodeling Inc. Can be made by check, 
              bank transfer or Zelle to zelle@setupremodeling.com, also cash, as follows:
                Down payment: 50% to begin the work.
                Second payment: 40% when more than 70% of the project is complete.
                Final payment: 10% of the completed project value.`}
            </Text>
          </View>
          {/** Notes to customer */}
          <View style={styles.notesView}>
            <Text style={styles.infoText}>Note:</Text>
            <Text style={styles.infoText}>
              {estimate?.notes || "No additional notes provided."}
            </Text>
          </View>
          <HR />
          <View style={styles.acceptView}>
            <View style={styles.acceptSpacedView}>
              <Text style={styles.boldText}>Accepted date</Text>
              <Text style={styles.infoText}>
                {DateConverter.toFullDate(estimate.acceptedAt)}
              </Text>
            </View>
            <View style={styles.acceptSpacedView}>
              <Text style={styles.boldText}>Accepted by</Text>
              <Text style={styles.infoText}>{estimate.acceptedBy}</Text>
            </View>
          </View>
          {/** Page Number */}
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} / ${totalPages}`
            }
            fixed
          />
        </Page>
      )}
    </Document>
  );
};

export default EstimatePreview;
